import {
  Card,
  CardContent,
  FormHelperText,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { hasPermission, isSuperUser } from "@saleor/auth/misc";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ControlledCheckbox } from "@saleor/components/ControlledCheckbox";
import FormSpacer from "@saleor/components/FormSpacer";
import SingleAutocompleteSelectField, {
  SingleAutocompleteChoiceType,
  SingleAutocompleteChoiceTypes
} from "@saleor/components/SingleAutocompleteSelectField";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { BrandErrorFragment } from "@saleor/fragments/types/BrandErrorFragment";
import { ChangeEvent, FormChange } from "@saleor/hooks/useForm";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import useStaffMemberSearch from "@saleor/searches/useStaffMemberSearch";
import { FetchMoreProps } from "@saleor/types";
import {
  BrandVerticalEnum,
  PermissionEnum,
  ShopifyTagEnum,
  SmopUserTypeCode,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getBrandErrorMessage from "@saleor/utils/errors/brand";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { SellerDetails_brandPrivate } from "../../types/SellerDetails";
import SellerDeepLink from "../SellerDeepLink";
import { SellerDetailsPageFormData } from "../SellerDetailsPage";
import { SellerLogo } from "../SellerLogo";
import ShopifySellerDetails from "../ShopifySellerDetails";
import ShopifyTags from "../ShopifyTags";
interface SellerInfoProps {
  handleShopifySync?: () => void;
  isShopifySyncLoading?: boolean;
  handleVerifyShopifyConnection?: (
    shopifyApiSecret: string,
    shopifyApiShop: string
  ) => void;
  isShopifyVerifyCredentialsLoading?: boolean;
  hasShopifyPermission?: boolean;
  createSeller?: boolean;
  data: SellerDetailsPageFormData;
  sellerData?: SellerDetails_brandPrivate;
  errors: BrandErrorFragment[];
  clientError?: {
    bankName: string;
    bankAccountName: string;
    bankAccountNo: string;
    financeName: string;
    financeEmail: string;
    financeMobileNo: string;
    legalName: string;
    name: string;
    signatoryName: string;
    signatoryEmail: string;
    signatoryDesignation: string;
    signatoryMobileNo: string;
    storeType: string;
    tinNo: string;
    vertical: string;
  };
  updateStatusError?: {
    customerCode?: string;
    vendorCode?: string;
    vertical?: string;
  };
  clusterDisplayValue: string;
  disabled: boolean;
  onChange: FormChange;
  sellerDisplayValue: string;
  variant?: string;
  seller: SingleAutocompleteChoiceTypes[];
  clusters: SingleAutocompleteChoiceType[];
  fetchBrands: (query: string) => void;
  fetchMoreBrands?: FetchMoreProps;
  fetchClusters: (query: string) => void;
  fetchMoreClusters?: FetchMoreProps;
  onClusterChange: (event: ChangeEvent) => void;
  onSellerChange: (event: ChangeEvent) => void;
  onImageDelete?: () => void;
  onImageUpload?: (file: File) => void;
  onGenerateDeeplink: (id: string) => void;
  onFileSizeError: () => void;
  onTagAdd?: (tag: string, type: ShopifyTagEnum) => void;
  onTagRemove?: (tag: string, type: ShopifyTagEnum) => void;
}

const useStyles = makeStyles(
  theme => ({
    card: {
      overflow: "visible"
    },
    cardTitle: {
      "& span": {
        fontWeight: "normal"
      }
    },
    errorText: {
      color: "#EC5D77",
      fontSize: "0.75rem",
      margin: "6px 14px"
    },
    label: {
      marginBottom: theme.spacing(1)
    },
    root: {
      overflow: "visible"
    },
    sectionTitle: {
      fontWeight: 600 as 600,
      marginBottom: theme.spacing(),
      marginTop: theme.spacing(2)
    },
    textFieldGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    },
    value: {
      fontSize: 20
    }
  }),
  { name: "SellerInfo" }
);

const SellerInfo: React.FC<SellerInfoProps> = ({
  clusters,
  clusterDisplayValue,
  fetchClusters,
  fetchMoreClusters,
  disabled,
  onChange,
  data,
  errors,
  clientError,
  updateStatusError,
  sellerDisplayValue,
  seller,
  variant,
  sellerData,
  fetchMoreBrands,
  fetchBrands,
  onSellerChange,
  onClusterChange,
  onImageDelete,
  onImageUpload,
  onGenerateDeeplink,
  onFileSizeError,
  handleShopifySync,
  isShopifySyncLoading,
  handleVerifyShopifyConnection,
  isShopifyVerifyCredentialsLoading,
  hasShopifyPermission,
  createSeller,
  onTagAdd,
  onTagRemove
}) => {
  const user = useUser();
  const status =
    data?.status === "RVW"
      ? "UNDER REVIEW"
      : data?.status === "ACT"
      ? "ACTIVE"
      : data?.status === "DCL"
      ? "DECLINED"
      : data?.status === "INA"
      ? "DEACTIVATE"
      : true;

  function getStoreTypes(hasShopifyPermission) {
    const storeTypes = [
      { label: "General Merchandise", value: "GENERAL_MERCHANDISE" },
      { label: "Food and Beverages", value: "FOOD_AND_BEVERAGES" },
      { label: "Grocery", value: "GROCERY" }
    ];
    if (hasShopifyPermission) {
      storeTypes.push({
        label: "ShopSM",
        value: StoreTypeEnum.SM_FULFILLED
      });
    }

    return storeTypes;
  }

  const storeTypes = getStoreTypes(hasShopifyPermission);

  const verticalChoices = [
    { label: "Retail Affiliates", value: BrandVerticalEnum.RETAIL_AFFILIATES },
    { label: "Marketplace", value: BrandVerticalEnum.MARKETPLACE },
    { label: "Cyberzone", value: BrandVerticalEnum.CYBERZONE }
    // {
    //   label: "Food and Beverages",
    //   value: BrandVerticalEnum.FOOD_AND_BEVERAGES
    // },
    // { label: "SM Fulfilled", value: BrandVerticalEnum.SM_FULFILLED },
    // { label: "Grocery", value: BrandVerticalEnum.GROCERY }
  ];

  const DEFAULT_KAMS_DATA = {
    after: null,
    first: 20,
    query: "",
    smopUserType: [SmopUserTypeCode.ACCOUNT_MANAGER]
  };

  const {
    search: onSearchChange,
    result: searchResult,
    loadMore
  } = useStaffMemberSearch({
    variables: DEFAULT_KAMS_DATA
  });
  const accountManagerChoices = maybe(
    () => searchResult?.data?.search?.edges,
    []
  ).map(edge =>
    edge
      ? {
          label: edge.node.firstName + " " + edge.node.lastName,
          value: edge.node.id
        }
      : {
          label: "",
          value: ""
        }
  );

  const formFields = [
    "legalName",
    "name",
    "storeType",
    "tinNo",
    "mobileNo",
    "signatoryName",
    "signatoryDesignation",
    "signatoryEmail",
    "signatoryMobileNo",
    "bankName",
    "bankAccountName",
    "bankAccountNo",
    "financeName",
    "financeEmail",
    "financeMobileNo",
    "customerCode",
    "vendorCode",
    "completeAddress",
    "vertical",
    "swornDeclarationsFlag"
  ];
  const classes = useStyles({});
  const intl = useIntl();
  const formErrors = getFormErrors(formFields, errors);
  const [accountManagerName, setAccountManagerName] = useState("");
  const handleAccountManager = (
    event: any,
    accountManager,
    onChange: (event: ChangeEvent<any>) => void
  ) => {
    const accMngr = accountManagerChoices.find(
      manager => manager.value === event.target.value
    );
    onChange({
      target: {
        name: accountManager,
        value: event.target.value
      }
    });
    setAccountManagerName(accMngr && accMngr.label);
  };
  useEffect(() => {
    if (
      data.accountManager &&
      data.accountManager.id &&
      accountManagerChoices &&
      accountManagerChoices.length
    ) {
      const accMngr = accountManagerChoices.find(
        manager => manager.value === data.accountManager.id
      );
      setAccountManagerName(accMngr && accMngr.label);
      onChange({
        target: {
          name: "accountManager",
          value: data.accountManager.id
        }
      });
    }
  }, [data.accountManager && data.accountManager.id, accountManagerChoices]);

  const isNewBrand = seller.find(e => e.label === sellerDisplayValue);
  const isFnB = data.storeType === StoreTypeEnum.FOOD_AND_BEVERAGES;

  const storeTypeProperCase = type => {
    switch (type) {
      case StoreTypeEnum.GENERAL_MERCHANDISE:
        return "General Merchandise";
      case StoreTypeEnum.FOOD_AND_BEVERAGES:
        return "Food and Beverages";
      case StoreTypeEnum.GROCERY:
        return "Grocery";
      case StoreTypeEnum.SM_FULFILLED:
        return "ShopSM";
      default:
        return " ";
    }
  };
  return (
    <>
      <div>
        <Card>
          <CardTitle
            className={classes.cardTitle}
            title={intl.formatMessage({
              defaultMessage: "Business Information",
              description: "Business Information"
            })}
          />
          <CardContent>
            <SingleAutocompleteSelectField
              displayValue={data.name || ""}
              label={intl.formatMessage({
                defaultMessage: "Brand Name"
              })}
              name="name"
              choices={seller}
              disabled={
                disabled ||
                user.user?.userType.code === SmopUserTypeCode.ACCOUNT_MANAGER
              }
              value={data.name || ""}
              onChange={onSellerChange}
              helperText={
                getBrandErrorMessage(formErrors.name, intl) || clientError?.name
              }
              error={!!formErrors.name || !!clientError?.name}
              fetchChoices={fetchBrands}
              {...fetchMoreBrands}
              customValueLabel="Create a New Brand"
              allowCustomValues
            />

            <FormSpacer />
            <TextField
              id="companyNameTxt"
              label={intl.formatMessage({
                defaultMessage: "Company Name",
                description: "Company Name"
              })}
              name="legalName"
              type="text"
              value={data.legalName || ""}
              onChange={onChange}
              disabled={
                disabled ||
                user.user?.userType.code === SmopUserTypeCode.ACCOUNT_MANAGER
              }
              error={!!formErrors.legalName || !!clientError?.legalName}
              helperText={
                getBrandErrorMessage(formErrors.legalName, intl) ||
                clientError?.legalName
              }
              fullWidth
            />
            <FormSpacer />
            <div className={classes.textFieldGrid}>
              <div>
                {data.name === null || isNewBrand === undefined ? (
                  <SingleSelectField
                    choices={storeTypes}
                    disabled={disabled || variant === "update"}
                    label={intl.formatMessage({
                      defaultMessage: "Classification (GM/F&B/Groceries)"
                    })}
                    name="storeType"
                    onChange={onChange}
                    value={data.storeType || ""}
                    error={!!formErrors.storyType || !!clientError?.storeType}
                  />
                ) : (
                  <TextField
                    id="classificationTxt"
                    fullWidth
                    disabled={true}
                    label={intl.formatMessage({
                      defaultMessage: "Classification (GM/F&B/Groceries)"
                    })}
                    name="storeType"
                    onChange={onChange}
                    value={storeTypeProperCase(
                      (data.storeType = seller?.[0]?.classification)
                    )}
                    error={!!formErrors.storyType || !!clientError?.storeType}
                  />
                )}
                {clientError?.storeType ? (
                  <FormHelperText className={classes.errorText}>
                    {clientError?.storeType}
                  </FormHelperText>
                ) : (
                  ""
                )}
              </div>
              <div>
                {data.name === null || isNewBrand === undefined ? (
                  <SingleSelectField
                    choices={verticalChoices}
                    disabled={
                      data.storeType !== "GENERAL_MERCHANDISE" ||
                      (variant === "update" &&
                        !isSuperUser(user.user) &&
                        !hasPermission(PermissionEnum.MANAGE_BRANDS, user.user))
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Vertical Type"
                    })}
                    name="vertical"
                    onChange={onChange}
                    value={
                      data.storeType === "GENERAL_MERCHANDISE"
                        ? data.vertical
                        : ""
                    }
                    error={
                      !!formErrors.vertical ||
                      !!clientError?.vertical ||
                      !!updateStatusError?.vertical
                    }
                  />
                ) : (
                  <SingleSelectField
                    choices={verticalChoices}
                    disabled={data.storeType ? true : false}
                    label={intl.formatMessage({
                      defaultMessage: "Vertical Type"
                    })}
                    name="vertical"
                    onChange={onChange}
                    value={
                      data.storeType === "GENERAL_MERCHANDISE"
                        ? (data.vertical = seller?.[0]?.vertical)
                        : ""
                    }
                    error={
                      !!formErrors.vertical ||
                      !!clientError?.vertical ||
                      !!updateStatusError?.vertical
                    }
                  />
                )}
                {clientError?.vertical || updateStatusError?.vertical ? (
                  <FormHelperText className={classes.errorText}>
                    {clientError?.vertical || updateStatusError?.vertical}
                  </FormHelperText>
                ) : (
                  ""
                )}
              </div>
            </div>
            <FormSpacer />
            <TextField
              id="tinNoTxt"
              label={intl.formatMessage({
                defaultMessage: "Tin No.",
                description: "Tin No"
              })}
              name="tinNo"
              type="text"
              value={data.tinNo || ""}
              onChange={onChange}
              disabled={disabled}
              error={!!formErrors.tinNo || !!clientError?.tinNo}
              helperText={
                getBrandErrorMessage(formErrors.tinNo, intl) ||
                clientError?.tinNo
              }
              fullWidth
            />
            <FormSpacer />
            <TextField
              id="completeAddressTxt"
              label={intl.formatMessage({
                defaultMessage: "Complete Address",
                description: "complete address"
              })}
              name="completeAddress"
              type="text"
              value={data.completeAddress || ""}
              disabled={disabled}
              onChange={onChange}
              helperText={getBrandErrorMessage(
                formErrors.completeAddress,
                intl
              )}
              error={!!formErrors.completeAddress}
              fullWidth
            />
            <FormSpacer />
            {user.user?.userType.code === "" ||
            user.user?.userType.code === "OMI_ADMIN" ? (
              <SingleAutocompleteSelectField
                label={intl.formatMessage({
                  defaultMessage: "Cluster:"
                })}
                name="cluster"
                displayValue={isFnB ? clusterDisplayValue : ""}
                choices={clusters}
                disabled={!isFnB}
                value={data.cluster || ""}
                onChange={onClusterChange}
                fetchChoices={fetchClusters}
                {...fetchMoreClusters}
              />
            ) : (
              ""
            )}
          </CardContent>
        </Card>
        <CardSpacer />
        {hasShopifyPermission && !createSeller && (
          <ShopifySellerDetails
            data={data}
            disabled={disabled}
            handleShopifySync={handleShopifySync}
            handleVerifyShopifyConnection={handleVerifyShopifyConnection}
            onChange={onChange}
            isLoading={isShopifySyncLoading}
            isShopifyVerifyCredentialsLoading={
              isShopifyVerifyCredentialsLoading
            }
          />
        )}
        <CardSpacer />
        {variant === "update" && (
          <>
            <ShopifyTags
              data={data}
              shopifyExclusionTags={""}
              shopifyNonCodTags={""}
              onChange={onChange}
              onAdd={onTagAdd}
              onRemove={onTagRemove}
            />
            <CardSpacer />
          </>
        )}
        <SellerDeepLink
          linkId={sellerData && sellerData.id}
          linkUrl={sellerData && sellerData.deeplinkUrl}
          shortLinkUrl={sellerData && sellerData.shortDynamicLink}
          onGenerateDeeplink={onGenerateDeeplink}
        />
        <CardSpacer />

        {variant === "update" ? (
          <SellerLogo
            data={data}
            image={sellerData?.logo}
            onChange={onChange}
            onImageDelete={onImageDelete}
            onImageUpload={onImageUpload}
            onFileSizeError={onFileSizeError}
          />
        ) : null}

        <CardSpacer />
        <Card>
          <CardTitle
            className={classes.cardTitle}
            title={intl.formatMessage({
              defaultMessage: "Authorized Signatory",
              description: "Authorized signatory"
            })}
          />
          <CardContent>
            <div className={classes.textFieldGrid}>
              <TextField
                id="signatoryNameTxt"
                label={intl.formatMessage({
                  defaultMessage: "Signatory Name",
                  description: "Signatory Name"
                })}
                name="signatoryName"
                type="text"
                disabled={disabled}
                value={data.signatoryName || ""}
                onChange={onChange}
                error={
                  !!formErrors.signatoryName || !!clientError?.signatoryName
                }
                helperText={
                  getBrandErrorMessage(formErrors.signatoryName, intl) ||
                  clientError?.signatoryName
                }
              />

              <TextField
                id="designationTxt"
                label={intl.formatMessage({
                  defaultMessage: "Designation",
                  description: "Designation"
                })}
                name="signatoryDesignation"
                type="text"
                disabled={disabled}
                value={data.signatoryDesignation || ""}
                onChange={onChange}
                error={
                  !!formErrors.signatoryDesignation ||
                  !!clientError?.signatoryDesignation
                }
                helperText={
                  getBrandErrorMessage(formErrors.signatoryDesignation, intl) ||
                  clientError?.signatoryDesignation
                }
              />
            </div>

            <FormSpacer />
            <div className={classes.textFieldGrid}>
              <TextField
                id="emailAddressTxt"
                label={intl.formatMessage({
                  defaultMessage: "Email Address",
                  description: "Email Address"
                })}
                name="signatoryEmail"
                type="text"
                disabled={disabled}
                value={data.signatoryEmail || ""}
                onChange={onChange}
                error={
                  !!formErrors.signatoryEmail || !!clientError?.signatoryEmail
                }
                helperText={
                  getBrandErrorMessage(formErrors.signatoryEmail, intl) ||
                  clientError?.signatoryEmail
                }
              />

              <TextField
                id="mobileNoTxt"
                label={intl.formatMessage({
                  defaultMessage: "Mobile No.",
                  description: "Mobile No."
                })}
                name="signatoryMobileNo"
                type="text"
                disabled={disabled}
                value={data.signatoryMobileNo || ""}
                onChange={onChange}
                error={
                  !!formErrors.signatoryMobileNo ||
                  !!clientError?.signatoryMobileNo
                }
                helperText={
                  getBrandErrorMessage(formErrors.signatoryMobileNo, intl) ||
                  clientError?.signatoryMobileNo
                }
              />
            </div>
          </CardContent>
        </Card>
        <CardSpacer />
        <Card>
          <CardTitle
            className={classes.cardTitle}
            title={intl.formatMessage({
              defaultMessage: "Bank Details",
              description: "Bank Details"
            })}
          />
          <CardContent>
            <TextField
              id="bankNameTxt"
              label={intl.formatMessage({
                defaultMessage: "Bank Name",
                description: "Bank Name"
              })}
              name="bankName"
              type="text"
              disabled={disabled}
              value={data.bankName || ""}
              onChange={onChange}
              error={!!formErrors.bankName || !!clientError?.bankName}
              helperText={
                getBrandErrorMessage(formErrors.bankName, intl) ||
                clientError?.bankName
              }
              fullWidth
            />
            <FormSpacer />
            <div className={classes.textFieldGrid}>
              <TextField
                id="accountNameTxt"
                label={intl.formatMessage({
                  defaultMessage: "Account Name",
                  description: "Account Name"
                })}
                name="bankAccountName"
                type="text"
                disabled={disabled}
                value={data.bankAccountName || ""}
                onChange={onChange}
                error={
                  !!formErrors.bankAccountName || !!clientError?.bankAccountName
                }
                helperText={
                  getBrandErrorMessage(formErrors.bankAccountName, intl) ||
                  clientError?.bankAccountName
                }
              />
              <TextField
                id="accountNoTxt"
                label={intl.formatMessage({
                  defaultMessage: "Account No.",
                  description: "Account No"
                })}
                name="bankAccountNo"
                type="text"
                disabled={disabled}
                value={data.bankAccountNo || ""}
                onChange={onChange}
                error={
                  !!formErrors.bankAccountNo || !!clientError?.bankAccountNo
                }
                helperText={
                  getBrandErrorMessage(formErrors.bankAccountNo, intl) ||
                  clientError?.bankAccountNo
                }
              />
            </div>
          </CardContent>
        </Card>
        <CardSpacer />
        <Card>
          <CardTitle
            className={classes.cardTitle}
            title={intl.formatMessage({
              defaultMessage: "Finance Contact Details",
              description: "Finance Contact Details"
            })}
          />
          <CardContent>
            <div className={classes.textFieldGrid}>
              <TextField
                id="financeNameTxt"
                label={intl.formatMessage({
                  defaultMessage: "Name",
                  description: "Finance Name"
                })}
                name="financeName"
                type="text"
                disabled={disabled}
                value={data.financeName || ""}
                onChange={onChange}
                error={!!formErrors.financeName || !!clientError?.financeName}
                helperText={
                  getBrandErrorMessage(formErrors.financeName, intl) ||
                  clientError?.financeName
                }
              />

              <TextField
                id="financeEmailAddressTxt"
                label={intl.formatMessage({
                  defaultMessage: "Email Address",
                  description: "Finance Email Address"
                })}
                name="financeEmail"
                type="text"
                disabled={disabled}
                value={data.financeEmail || ""}
                onChange={onChange}
                error={!!formErrors.financeEmail || !!clientError?.financeEmail}
                helperText={
                  getBrandErrorMessage(formErrors.financeEmail, intl) ||
                  clientError?.financeEmail
                }
              />
            </div>
            <FormSpacer />
            <div className={classes.textFieldGrid}>
              <TextField
                id="financeMobileNoTxt"
                label={intl.formatMessage({
                  defaultMessage: "Mobile No.",
                  description: "Finance Mobil No"
                })}
                name="financeMobileNo"
                type="text"
                disabled={disabled}
                onChange={onChange}
                value={data.financeMobileNo || ""}
                error={
                  !!formErrors.financeMobileNo || !!clientError?.financeMobileNo
                }
                helperText={
                  getBrandErrorMessage(formErrors.financeMobileNo, intl) ||
                  clientError?.financeMobileNo
                }
              />
            </div>
          </CardContent>
        </Card>
        <CardSpacer />
        {user.user?.userType.code !== SmopUserTypeCode.ACCOUNT_MANAGER && (
          <Card>
            <CardTitle
              className={classes.cardTitle}
              title={intl.formatMessage({
                defaultMessage: "Account Manager",
                description: "Account Manager Details"
              })}
            />
            <CardContent>
              <div>
                <SingleAutocompleteSelectField
                  displayValue={accountManagerName}
                  disabled={disabled}
                  label={intl.formatMessage({
                    defaultMessage: "KAMS"
                  })}
                  choices={accountManagerChoices}
                  onChange={value =>
                    handleAccountManager(value, "accountManager", onChange)
                  }
                  name={"accountManager" as keyof SellerDetailsPageFormData}
                  value={data?.accountManager || ""}
                  fetchChoices={onSearchChange}
                  onFetchMore={loadMore}
                  data-test="accountManager"
                  hasMore={searchResult?.data?.search?.pageInfo.hasNextPage}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <div>
        <Card>
          <CardTitle
            className={classes.cardTitle}
            title={intl.formatMessage({
              defaultMessage: "Seller Status",
              description: "Status of Seller"
            })}
          />
          <CardContent>
            <Typography className={classes.label} variant="caption">
              <FormattedMessage defaultMessage="Status" />
            </Typography>
            <Typography
              variant="h6"
              className={classes.value}
              id="sellerStatusValue"
            >
              <FormattedMessage
                defaultMessage="{status}"
                values={{
                  status: <strong>{status}</strong>
                }}
              />
            </Typography>
          </CardContent>
        </Card>

        <CardSpacer />

        <Card>
          <CardTitle
            className={classes.cardTitle}
            title={intl.formatMessage({
              defaultMessage: "Internal Details",
              description: "internal details"
            })}
          />
          <CardContent>
            <TextField
              id="customerCodeTxt"
              label={intl.formatMessage({
                defaultMessage: "Customer Code",
                description: "customer code"
              })}
              name="customerCode"
              type="text"
              disabled={disabled}
              onChange={onChange}
              value={data.customerCode || ""}
              error={
                !!formErrors.customerCode || !!updateStatusError?.customerCode
              }
              helperText={
                getBrandErrorMessage(formErrors.customerCode, intl) ||
                updateStatusError?.customerCode
              }
              fullWidth
            />
            <FormSpacer />
            <TextField
              id="vendorCodeTxt"
              label={intl.formatMessage({
                defaultMessage: "Vendor Code",
                description: "vendor code"
              })}
              name="vendorCode"
              type="text"
              disabled={disabled}
              onChange={onChange}
              value={data.vendorCode || ""}
              error={!!formErrors.vendorCode || !!updateStatusError?.vendorCode}
              helperText={
                getBrandErrorMessage(formErrors.vendorCode, intl) ||
                updateStatusError?.vendorCode
              }
              fullWidth
            />
            <FormSpacer />
            <div>
              <ControlledCheckbox
                id="largeTaxpayerCheckbox"
                name="isLargeTaxpayer"
                label={intl.formatMessage({
                  defaultMessage: "Large Taxpayer?",
                  description: "switch button"
                })}
                checked={data.isLargeTaxpayer}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlledCheckbox
                id="payoutEligibleCheckbox"
                name="isPayoutEligible"
                label={intl.formatMessage({
                  defaultMessage: "Payout eligible?",
                  description: "switch button"
                })}
                checked={data.isPayoutEligible}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
            <div>
              <ControlledCheckbox
                id="swornDeclarationsFlagCheckbox"
                name="swornDeclarationsFlag"
                label={intl.formatMessage({
                  defaultMessage: "Sworn Declaration on Gross Settlement",
                  description: "switch button"
                })}
                checked={data.swornDeclarationsFlag}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

SellerInfo.displayName = "SellerInfo";
export default SellerInfo;
